import { FitLine, FitZone } from './fitText';

/**
 * The one-ink pad — the cheapest book the press runs: white stock, no colour
 * work, the shop's name set large over a grey heading, the proprietor in a
 * solid plate, a band across the sheet for the trade line, and a ruled grid
 * with মোটঃ / জমাঃ / বাকিঃ boxed into its corner.
 *
 * Because it is printed in one ink, that ink is whatever colour the shop
 * chooses: the filled blocks, the rules and the type all follow it. Picking
 * black gives the pad the press actually runs.
 *
 * Measured off a printed pad in A4 pixels and multiplied by `u` (the paper's
 * width against A4), so an A6 book is the same pad printed small.
 */

/** The heading block's height, as a share of the paper's width. */
const HEAD_RATIO = 0.345;

/** The words this pad prints that the other styles word differently. */
const PLAIN_LABELS = {
    bn: { memoNo: 'ক্রমিক নং', inWords: 'কথায়', buyerName: 'ক্রেতার নাম', mobile: 'মোবাইল', colon: 'ঃ' },
    en: { memoNo: 'SL No.', inWords: 'In words', buyerName: "Buyer's name", mobile: 'Mobile', colon: ':' },
};

/** "ক্রেতার নামঃ ......" — a label and the rule it is written on. */
function DottedLine({ label, u, ink }) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 * u }}>
            <span style={{ fontSize: 20 * u, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'nowrap', color: ink }}>
                {label}
            </span>
            <span style={{ flex: 1, borderBottom: `${2 * u}px dotted ${ink}`, height: 13 * u }} />
        </div>
    );
}

/** The ruled grid: a tinted head over an open body, all in the one ink. */
function PlainGrid({ memo, u, ink, tint }) {
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.3 * u}px solid ${ink}`;
    const last = memo.columns.length - 1;

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {memo.columns.map((col, i) => (
                        <th
                            key={col.id}
                            style={{
                                background: tint,
                                color: ink,
                                height: 40 * u,
                                padding: `0 ${3 * u}px`,
                                fontSize: 20 * u,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                textAlign: 'center',
                                borderTop: rule,
                                borderBottom: rule,
                                borderLeft: (memo.columnLines || i === 0) ? rule : 'none',
                                borderRight: (memo.columnLines || i === last) ? rule : 'none',
                            }}
                        >
                            <span>{col.label || ' '}</span>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: memo.rows }, (_, i) => (
                    <tr key={i}>
                        {memo.columns.map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    height: 30 * u,
                                    padding: 0,
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: (memo.columnLines || c === last) ? rule : 'none',
                                    borderBottom: memo.rowLines && i < memo.rows - 1 ? rule : 'none',
                                }}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** মোটঃ / জমাঃ / বাকিঃ, boxed into the corner of the same grid. */
function PlainTotals({ memo, labels, u, ink, tint }) {
    const rows = [
        memo.totals.total ? labels.total : null,
        memo.totals.advance ? labels.advance : null,
        memo.totals.due ? labels.due : null,
    ].filter(Boolean);

    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.3 * u}px solid ${ink}`;
    const span = Math.max(0, memo.columns.length - 2);

    if (!rows.length) {
        return <div style={{ borderTop: rule, borderLeft: rule, borderRight: rule, height: 0 }} />;
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <tbody>
                {rows.map((label, i) => {
                    const lastRow = i === rows.length - 1;
                    return (
                        <tr key={label}>
                            {memo.columns.slice(0, span).map((col, c) => (
                                <td
                                    key={col.id}
                                    style={{
                                        borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                        borderRight: memo.columnLines && c === span - 1 ? rule : 'none',
                                        borderBottom: lastRow ? rule : 'none',
                                        height: 32 * u,
                                        padding: 0,
                                    }}
                                />
                            ))}
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    background: tint,
                                    height: 32 * u,
                                    padding: `0 ${8 * u}px`,
                                    fontSize: 19 * u,
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    textAlign: 'right',
                                    color: ink,
                                }}
                            >
                                <span>{`${label}${PLAIN_LABELS.bn.colon}`}</span>
                            </td>
                            <td style={{ border: rule, borderBottom: lastRow ? rule : 'none', height: 32 * u, padding: 0 }} />
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/* ------------------------------------------------------------------ *
 *  The sheet
 * ------------------------------------------------------------------ */

export default function PlainMemo({ memo, labels, title, accent, paperWidth, u }) {
    const { shop, show } = memo;
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const words = PLAIN_LABELS[lang];
    const on = (key) => show[key] !== false;
    // One ink, and it is the one the shop picked: black gives the pad the
    // press runs, any other colour the same pad in that colour.
    const ink = accent || '#111111';
    const pad = 36 * u;
    const headHeight = Math.round(paperWidth * HEAD_RATIO);
    const headPad = { top: 14 * u, side: 36 * u, bottom: 10 * u };
    const phones = [shop.phone, shop.phone2].filter((n) => String(n || '').trim());
    const email = String(shop.email || '').trim();
    const addressLine = shop.address.split('\n').map((l) => l.trim()).filter(Boolean).join(', ');
    const contactLine = [
        phones.length ? `${words.mobile}${words.colon} ${phones.join(', ')}` : '',
        on('email') ? email : '',
    ].filter(Boolean).join('   ');
    const headKey = [
        memo.bismillah, title, shop.name, memo.distributor, memo.proprietor, addressLine,
        contactLine, shop.logo ? '1' : '0', JSON.stringify(show), u,
    ].join('~');

    return (
        <>
            {/* ---- the heading, a fixed block on grey stock ---- */}
            <div style={{ position: 'relative', flex: 'none', background: '#F1F1F1' }}>
                {on('logo') && shop.logo ? (
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'absolute', left: pad, top: 14 * u, zIndex: 1,
                            width: 120 * u, height: 58 * u,
                            backgroundImage: `url("${shop.logo}")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'left center',
                            backgroundSize: 'contain',
                        }}
                    />
                ) : null}

                <FitZone height={headHeight} align="centre" pad={headPad} signature={headKey}>
                    <div style={{ textAlign: 'center', color: ink }}>
                        {on('bismillah') && memo.bismillah.trim() ? (
                            <div style={{ fontSize: 16 * u, fontWeight: 500, lineHeight: 1.3 }}>
                                {memo.bismillah}
                            </div>
                        ) : null}

                        <FitLine
                            text={shop.name || ' '}
                            size={70 * u}
                            style={{ marginTop: 4 * u, fontWeight: 800, lineHeight: 1.1 }}
                        />

                        {on('distributor') && memo.distributor.trim() ? (
                            <FitLine
                                text={memo.distributor}
                                size={19 * u}
                                style={{ marginTop: 2 * u, fontWeight: 500, lineHeight: 1.35 }}
                            />
                        ) : null}

                        {/* The proprietor in a solid plate, as the block is cut. */}
                        {on('proprietor') && memo.proprietor.trim() ? (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 7 * u }}>
                                <div
                                    style={{
                                        maxWidth: '78%', background: ink, color: '#FFFFFF',
                                        borderRadius: 4 * u, padding: `${3 * u}px ${22 * u}px`,
                                    }}
                                >
                                    <FitLine
                                        text={memo.proprietor}
                                        size={22 * u}
                                        style={{ fontWeight: 700, lineHeight: 1.35, textAlign: 'center' }}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {on('addressBand') && addressLine ? (
                            <FitLine
                                text={`${labels.address}${words.colon} ${addressLine}`}
                                size={22 * u}
                                style={{ marginTop: 7 * u, fontWeight: 600, lineHeight: 1.4 }}
                            />
                        ) : null}

                        {on('phone') && contactLine ? (
                            <FitLine
                                text={contactLine}
                                size={22 * u}
                                style={{ marginTop: 2 * u, fontWeight: 600, lineHeight: 1.4 }}
                            />
                        ) : null}
                    </div>
                </FitZone>
            </div>

            {/* ---- the trade line, banded across the sheet ---- */}
            {on('tagline') && shop.tagline.trim() ? (
                <div
                    style={{
                        flex: 'none', background: ink, color: '#FFFFFF', textAlign: 'center',
                        padding: `${6 * u}px ${pad}px`,
                    }}
                >
                    <FitLine text={shop.tagline} size={22 * u} style={{ fontWeight: 600, lineHeight: 1.4 }} />
                </div>
            ) : null}

            {/* ---- the form ---- */}
            <div
                style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    padding: `${20 * u}px ${pad}px ${22 * u}px`, boxSizing: 'border-box', color: ink,
                }}
            >
                {(memo.fields.memoNo || memo.fields.date) ? (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20 * u, marginBottom: 14 * u }}>
                        <span style={{ flex: 1, fontSize: 20 * u, fontWeight: 600, lineHeight: 1.4 }}>
                            {memo.fields.memoNo ? `${words.memoNo}${words.colon}` : ''}
                        </span>
                        {memo.fields.date ? (
                            <span style={{ flex: 1, fontSize: 20 * u, fontWeight: 600, lineHeight: 1.4 }}>
                                {`${labels.date}${words.colon}`}
                            </span>
                        ) : null}
                    </div>
                ) : null}

                {memo.fields.name ? (
                    <DottedLine label={`${words.buyerName}${words.colon}`} u={u} ink={ink} />
                ) : null}
                {memo.fields.address ? (
                    <div style={{ marginTop: 8 * u }}>
                        <DottedLine label={`${labels.address}${words.colon}`} u={u} ink={ink} />
                    </div>
                ) : null}

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 12 * u }}>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
                        <PlainGrid memo={memo} u={u} ink={ink} tint="#EFEFEF" />
                    </div>
                    <PlainTotals memo={memo} labels={labels} u={u} ink={ink} tint="#EFEFEF" />

                    {on('slogan') && memo.slogan.trim() ? (
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'absolute', left: '8%', right: '30%', bottom: '10%',
                                textAlign: 'center', pointerEvents: 'none',
                                fontSize: 28 * u, fontWeight: 600, lineHeight: 1.2,
                                color: ink, opacity: 0.12, whiteSpace: 'nowrap',
                                transform: 'skewX(-10deg)',
                            }}
                        >
                            {memo.slogan}
                        </div>
                    ) : null}
                </div>

                {memo.showWords ? (
                    <div style={{ marginTop: 14 * u }}>
                        <DottedLine label={`${words.inWords}${words.colon}`} u={u} ink={ink} />
                    </div>
                ) : null}

                <div style={{ flex: 'none', minHeight: 34 * u }} />

                {/* The shop's own note, set small between the grid and the two
                    signatures — where this pad prints its terms. */}
                {memo.footerNote ? (
                    <div style={{ textAlign: 'center', fontSize: 16 * u, fontWeight: 500, lineHeight: 1.4, opacity: 0.75 }}>
                        {memo.footerNote}
                    </div>
                ) : null}

                {memo.showSignature ? (
                    <div
                        style={{
                            marginTop: 10 * u, display: 'flex', alignItems: 'flex-end',
                            justifyContent: 'space-between', gap: 30 * u,
                            fontSize: 20 * u, fontWeight: 600, lineHeight: 1.35,
                        }}
                    >
                        <span>{labels.customerSign}</span>
                        <span>{labels.sellerSign}</span>
                    </div>
                ) : null}
            </div>
        </>
    );
}
